import { attr, fk, Model } from 'redux-orm';
import {
  ACTIVITY,
  ACTIVITY_LABEL,
  DISBURSE_TYPE_P2P_DIRECT_CODE,
  VENMO_DIRECT,
} from 'consts';
import {
  createAction,
  createLoadConst,
  createRequestConst,
  isOn,
} from '../util';
import Payer from './payer';

const getDisplayName = (type) => {
  switch (type) {
    case ACTIVITY:
      return ACTIVITY_LABEL;

    case DISBURSE_TYPE_P2P_DIRECT_CODE:
      return VENMO_DIRECT;

    default:
      return type?.toUpperCase() ?? ACTIVITY_LABEL;
  }
};

const inProgressRefreshDdaStatuses = new Set(['queued', 'running', 'starting']);
const palViewAsRefreshFallbackStatuses = new Set([
  'disabled',
  'expired',
  'failed',
  'not_eligible',
  'timeout',
  'unavailable',
  'unknown',
]);

const getRefreshDdaStatus = (dda) =>
  typeof dda?.status === 'string' ? dda.status.toLowerCase() : 'unknown';

export const isPalViewAsRefreshFallback = (status) =>
  typeof status === 'string' &&
  palViewAsRefreshFallbackStatuses.has(status.toLowerCase());

export const getPalViewAsRefreshRetryDdaIds = (refresh) => {
  const response = refresh?.response ?? refresh ?? {};
  const ddas = Array.isArray(response.ddas) ? response.ddas : [];
  return ddas
    .filter((dda) => {
      const status = getRefreshDdaStatus(dda);
      return (
        !inProgressRefreshDdaStatuses.has(status) &&
        !(status === 'complete' && dda?.fresh === true)
      );
    })
    .map((dda) => dda?.id)
    .filter((id) => typeof id === 'string' && id.length > 0);
};

const getURL = ({ props, response }) => {
  const { fspId, payerId } = props;
  const { sso } = response;
  const { ssoToken, uri } = sso;

  return `${uri}?ssoToken=${ssoToken}&fspId=${fspId}&payerId=${payerId}`;
};
const getURLs = (data) => {
  const url = getURL(data);
  const { sso } = data.response;
  const { links } = sso;

  const urls = links
    .filter(({ defaultUri }) => isOn(defaultUri))
    .map(({ type }, id) => ({
      id,
      name: getDisplayName(type),
      url: `${url}&type=${type}`,
    }));

  if (urls.length === 0) {
    const defaultURL = {
      id: 0,
      name: getDisplayName(),
      url: `${url}&type=${ACTIVITY}`,
    };

    urls.push(defaultURL);
  }

  return urls;
};

const sanitizePalViewAsRefreshDda = (dda = {}) => {
  const sanitized = {};
  const id =
    typeof dda.id === 'string'
      ? dda.id
      : typeof dda.ddaId === 'string'
      ? dda.ddaId
      : undefined;

  if (id !== undefined) {
    sanitized.id = id;
  }
  if (typeof dda.type === 'string') {
    sanitized.type = dda.type;
  }
  if (typeof dda.status === 'string') {
    sanitized.status = dda.status;
  }
  if (typeof dda.fresh === 'boolean') {
    sanitized.fresh = dda.fresh;
  }
  if (typeof dda.updatedAt === 'string' && Date.parse(dda.updatedAt) > 0) {
    sanitized.updatedAt = dda.updatedAt;
  }
  return sanitized;
};

export const sanitizePalViewAsRefreshResponse = (response = {}) => {
  const source =
    response?.data && typeof response.data === 'object'
      ? response.data
      : response;
  const sanitized = {};

  if (typeof source.status === 'string') {
    sanitized.status = source.status;
  }
  if (typeof source.fresh === 'boolean') {
    sanitized.fresh = source.fresh;
  }
  if (Array.isArray(source.ddas)) {
    sanitized.ddas = source.ddas.map(sanitizePalViewAsRefreshDda);
  }

  return sanitized;
};

const registrationData = (data = {}, previous = {}) => ({
  fspId: data.fspId ?? previous.fspId,
  payerId: data.payerId ?? previous.payerId,
  userId: data.userId ?? previous.userId,
  payerUser: data.payerUser ?? previous.payerUser,
  palRefreshEnabled:
    data.palRefreshEnabled === true || previous.palRefreshEnabled === true,
  requestId: data.requestId ?? previous.requestId,
  status: data.status ?? previous.status,
});

const getActionContext = (action) =>
  action.reduxActionData ?? action.data?.props ?? action.data ?? {};

const getPayerId = (data = {}) => data.payerId ?? data.props?.payerId;

const isCurrentRegistrationRequest = (registration, requestId) =>
  (registration?.requestId ?? null) === (requestId ?? null);

const isCurrentRequest = (viewAs, requestId) =>
  !requestId ||
  !viewAs.registration?.requestId ||
  viewAs.registration.requestId === requestId;

const mergeRefreshDdas = (previousDdas = [], nextDdas = []) => {
  const previousById = new Map(
    previousDdas.filter((dda) => dda?.id).map((dda) => [dda.id, dda])
  );
  const nextById = new Map(
    nextDdas.filter((dda) => dda?.id).map((dda) => [dda.id, dda])
  );
  const keepUpdatedAt = (dda) => {
    const updatedAt = previousById.get(dda.id)?.updatedAt;
    return dda.updatedAt || !updatedAt ? dda : { ...dda, updatedAt };
  };
  const retained = previousDdas.filter((dda) => !nextById.has(dda?.id));
  const nextWithoutIds = nextDdas.filter((dda) => !dda?.id);
  const retainedWithoutIds = retained.filter((dda) => !dda?.id);
  const knownRetained = retained.filter((dda) => dda?.id);
  const uniqueUnidentified = [...retainedWithoutIds, ...nextWithoutIds].filter(
    (dda, index, records) =>
      records.findIndex(
        (candidate) => JSON.stringify(candidate) === JSON.stringify(dda)
      ) === index
  );
  return [
    ...knownRetained,
    ...nextDdas.filter((dda) => dda?.id).map(keepUpdatedAt),
    ...uniqueUnidentified,
  ];
};

const setRefreshResponse = (
  viewAs,
  requestId,
  response,
  preservePreviousDdas = true,
  pollingComplete = false
) => {
  if (!isCurrentRequest(viewAs, requestId)) {
    return;
  }

  const sanitizedResponse = sanitizePalViewAsRefreshResponse(response);
  const previousDdas = viewAs.refresh?.response?.ddas;
  if (
    preservePreviousDdas &&
    Array.isArray(previousDdas) &&
    Array.isArray(sanitizedResponse.ddas)
  ) {
    sanitizedResponse.ddas = mergeRefreshDdas(
      previousDdas,
      sanitizedResponse.ddas
    );
  }
  viewAs.update({
    refresh: {
      requestId: requestId ?? viewAs.registration?.requestId,
      ...(pollingComplete ? { pollingComplete: true } : {}),
      response: sanitizedResponse,
    },
  });
};

export default class ViewAs extends Model {
  static get modelName() {
    return 'ViewAs';
  }

  static reducer(action, ViewAs) {
    const { type, data } = action;
    switch (type) {
      case LOAD_PAYER.ACTION:
      case LOAD_PAYER_USER.ACTION: {
        const payerId = getPayerId(data);
        if (payerId != null) {
          ViewAs.upsert({
            payerId,
            urls: [],
            registration: registrationData({ ...data, status: 'loading' }),
            refresh: null,
          });
        }
        break;
      }

      case LOAD_PAYER.SUCCESS:
      case LOAD_PAYER_USER.SUCCESS: {
        const context = getActionContext(action);
        const { payerId } = data?.props ?? context;
        const hasPrevious = ViewAs.idExists(payerId);
        const previous = hasPrevious
          ? ViewAs.withId(payerId).registration ?? {}
          : {};
        if (
          hasPrevious &&
          !isCurrentRegistrationRequest(previous, context.requestId)
        ) {
          break;
        }
        const urls = getURLs(data);

        ViewAs.upsert({
          payerId,
          urls,
          registration: registrationData(
            {
              ...context,
              ...data?.props,
              status: 'success',
            },
            previous
          ),
          refresh: null,
        });
        break;
      }

      case LOAD_PAYER.FAILURE:
      case LOAD_PAYER_USER.FAILURE: {
        const context = getActionContext(action);
        const payerId = getPayerId(context);
        if (payerId != null) {
          const hasPrevious = ViewAs.idExists(payerId);
          const previous = hasPrevious
            ? ViewAs.withId(payerId).registration ?? {}
            : {};
          if (
            hasPrevious &&
            !isCurrentRegistrationRequest(previous, context.requestId)
          ) {
            break;
          }
          const registration = registrationData(
            { ...context, status: 'failed' },
            previous
          );
          ViewAs.upsert({
            payerId,
            urls: [],
            registration,
            refresh:
              registration.palRefreshEnabled === true
                ? {
                    requestId: registration.requestId,
                    response: {
                      status: 'failed',
                      fresh: false,
                      ddas: [],
                    },
                  }
                : null,
          });
        }
        break;
      }

      case PAL_REFRESH.ACTION: {
        const { payerId, requestId } = data ?? {};
        if (payerId != null && ViewAs.idExists(payerId)) {
          const viewAs = ViewAs.withId(payerId);
          if (requestId) {
            viewAs.update({
              registration: registrationData(
                { ...data, status: 'success' },
                viewAs.registration ?? {}
              ),
              refresh: {
                requestId,
                response: {
                  status: 'starting',
                  fresh: false,
                  ddas: viewAs.refresh?.response?.ddas ?? [],
                },
              },
            });
          }
        } else if (payerId != null) {
          const { fspId, userId } = data;
          ViewAs.upsert({
            payerId,
            urls: [],
            registration: registrationData({
              fspId,
              payerId,
              userId,
              requestId,
              status: 'success',
            }),
            refresh: {
              requestId,
              response: { status: 'starting', fresh: false, ddas: [] },
            },
          });
        }
        break;
      }

      case PAL_REFRESH.SUCCESS:
      case PAL_REFRESH.STATUS: {
        const { payerId, pollingComplete, requestId, response } = data ?? {};
        if (payerId != null && ViewAs.idExists(payerId)) {
          setRefreshResponse(
            ViewAs.withId(payerId),
            requestId,
            response,
            true,
            pollingComplete === true
          );
        }
        break;
      }

      case PAL_REFRESH.FAILURE: {
        const { payerId, requestId, status } = data ?? {};
        if (payerId != null && ViewAs.idExists(payerId)) {
          const viewAs = ViewAs.withId(payerId);
          if (isCurrentRequest(viewAs, requestId)) {
            const currentResponse = viewAs.refresh?.response;
            const retainedDdas = Array.isArray(currentResponse?.ddas)
              ? currentResponse.ddas.map(sanitizePalViewAsRefreshDda)
              : [];
            setRefreshResponse(
              viewAs,
              requestId,
              {
                status: status ?? 'unavailable',
                fresh: false,
                ddas: retainedDdas,
              },
              false
            );
          }
        }
        break;
      }

      default:
        break;
    }
  }

  /**
   * Declaring all data fields is recommended as the library doesn't have to
   * redefine getters and setters when instantiating Models
   * */
  static get fields() {
    return {
      payerId: fk(Payer.modelName, 'viewAs'),
      urls: attr(),
      registration: attr(),
      refresh: attr(),
    };
  }

  /**
   * Use payerId as key
   * */
  static options = {
    idAttribute: 'payerId',
  };
}

export const LOAD_PAYER = createLoadConst(ViewAs.modelName);
export const LOAD_PAYER_USER = createLoadConst(`${ViewAs.modelName}_USER`);
export const PAL_REFRESH = {
  ...createRequestConst(`${ViewAs.modelName}_PAL_REFRESH`),
  STATUS: 'VIEWAS_PAL_REFRESH_STATUS',
  CANCEL: 'VIEWAS_PAL_REFRESH_CANCEL',
};

export const loadPayer = createAction(LOAD_PAYER);
export const loadPayerUser = createAction(LOAD_PAYER_USER);
export const startPalViewAsRefresh = createAction(PAL_REFRESH);
export const cancelPalViewAsRefresh = (data = {}) => ({
  type: PAL_REFRESH.CANCEL,
  data,
});
